import { useEffect, useRef } from 'react'

interface Options {
  highlightedStep?: number
  deps?: unknown[]
}

const SCROLL_DELAY_MS = 10
const VISIBILITY_THRESHOLD = 0.1

export function useCenterHighlightedStep({ highlightedStep, deps = [] }: Options) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map())

  useEffect(() => {
    const observerTarget = panelRef.current
    if (!observerTarget || !highlightedStep) return

    let scrollTimer: NodeJS.Timeout | null = null

    const scrollToHighlighted = () => {
      if (scrollTimer) clearTimeout(scrollTimer)

      scrollTimer = setTimeout(() => {
        const list = listRef.current
        const targetElement = itemRefs.current.get(highlightedStep)
        if (!list || !targetElement) return

        // Force layout recalculation to ensure accurate measurements
        list.scrollLeft = list.scrollLeft

        // Bounding rects
        const listRect = list.getBoundingClientRect()
        const targetRect = targetElement.getBoundingClientRect()

        // Current scroll & offset needed to center
        const currentScroll = list.scrollLeft
        const targetCenter = targetRect.left + targetRect.width / 2 - listRect.left
        const listCenter = listRect.width / 2
        const scrollOffset = targetCenter - listCenter

        list.scrollTo({
          left: currentScroll + scrollOffset,
          behavior: 'smooth',
        })
      }, SCROLL_DELAY_MS)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // Panel visible: perform the scroll (with delay)
            scrollToHighlighted()
          } else if (scrollTimer) {
            // Cancel pending scroll when panel hides
            clearTimeout(scrollTimer)
            scrollTimer = null
          }
        }
      },
      { threshold: VISIBILITY_THRESHOLD }
    )

    observer.observe(observerTarget)

    return () => {
      observer.disconnect()
      if (scrollTimer) clearTimeout(scrollTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally spread to allow dynamic dependencies for effect
  }, [highlightedStep, ...deps])

  return {
    panelRef,
    listRef,
    getItemRef: (step: number) => (el: HTMLDivElement | null) => {
      if (el) itemRefs.current.set(step, el)
      else itemRefs.current.delete(step)
    },
  }
}
